import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Interfaz para el estado de mantenimiento
 */
export interface MaintenanceStatus {
  id: number;
  created_at: string;
  in_maintenance: boolean;
  in_maintenance_sudo?: boolean;
  maintenance_message?: string;
  maintenance_url?: string;
}

/**
 * Configuración de Supabase
 */
export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

/**
 * Servicio de mantenimiento en tiempo real usando Supabase WebSocket
 * Migración desde Firebase a Supabase para el sistema de mantenimiento
 * Incluye funcionalidad de callback URL para redirigir al usuario después del mantenimiento
 */
@Injectable({
  providedIn: 'root'
})
export class MaintenanceRealtimeService {
  private socket: WebSocket | null = null;
  private isConnected = false;
  private currentStatus: MaintenanceStatus | null = null;
  private config: SupabaseConfig | null = null;
  
  
  // Subject para notificar cambios de estado
  private maintenanceStatusSubject = new BehaviorSubject<MaintenanceStatus | null>(null);
  public maintenanceStatus$ = this.maintenanceStatusSubject.asObservable();

  // Subject para estado de conexión
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  // Subject para notificar cuando se debe redirigir después del mantenimiento
  private redirectAfterMaintenanceSubject = new BehaviorSubject<string | null>(null);
  public redirectAfterMaintenance$ = this.redirectAfterMaintenanceSubject.asObservable();

  constructor() {}

  /**
   * Inicializa el servicio con la configuración de Supabase
   */
  async initialize(config: SupabaseConfig): Promise<void> {
    this.config = config;
    
    if (!config.url || !config.anonKey) {
      console.error('❌ Configuración de Supabase incompleta');
      return;
    }

    try {
      // Cargar estado inicial
      await this.loadInitialStatus();
      
      // Conectar WebSocket
      await this.connectRealtime();
    } catch (error) {
      console.error('❌ Error inicializando servicio de mantenimiento:', error);
    }
  }

  /**
   * Carga el estado inicial de mantenimiento via HTTP
   */
  private async loadInitialStatus(): Promise<void> {
    if (!this.config) return;

    try {
      const response = await fetch(`${this.config.url}/rest/v1/status?select=*&order=id.asc&limit=1`, {
        method: 'GET',
        headers: {
          'apikey': this.config.anonKey,
          'Authorization': `Bearer ${this.config.anonKey}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        this.currentStatus = data[0];
        this.maintenanceStatusSubject.next(this.currentStatus);
      }
    } catch (error) {
      console.error('❌ Error cargando estado inicial:', error);
    }
  }

  /**
   * Conecta al WebSocket de Supabase para recibir actualizaciones
   */
  private async connectRealtime(): Promise<void> {
    if (!this.config) return;

    try {
      // Crear conexión WebSocket
      const wsUrl = this.config.url.replace('https://', 'wss://').replace('http://', 'ws://');
      const socketUrl = `${wsUrl}/realtime/v1/websocket?apikey=${this.config.anonKey}&vsn=1.0.0`;
      
      this.socket = new WebSocket(socketUrl);
      
      this.socket.onopen = () => {
        this.isConnected = true;
        this.connectionStatusSubject.next(true);
        this.joinChannel();
      };

      this.socket.onclose = (event) => {
        this.isConnected = false;
        this.connectionStatusSubject.next(false);
        
        // Reconectar después de 5 segundos
        setTimeout(() => {
          if (!this.isConnected) {
            this.connectRealtime();
          }
        }, 5000);
      };

      this.socket.onerror = (error) => {
        console.error('❌ Error en WebSocket:', error);
      };

      this.socket.onmessage = (event) => {
        this.handleMessage(JSON.parse(event.data));
      };

    } catch (error) {
      console.error('❌ Error conectando WebSocket:', error);
    }
  }

  /**
   * Se une al canal de la tabla status
   */
  private joinChannel(): void {
    if (!this.socket) return;

    const joinMessage = {
      topic: 'realtime:public:status',
      event: 'phx_join',
      payload: {},
      ref: Date.now().toString()
    };

    this.socket.send(JSON.stringify(joinMessage));
  }

  /**
   * Maneja los mensajes del WebSocket
   */
  private handleMessage(message: any): void {
    switch (message.event) {
      case 'postgres_changes':
        this.handlePostgresChange(message.payload);
        break;
        
      case 'INSERT':
      case 'UPDATE':
      case 'DELETE':
        this.handlePostgresChange(message.payload);
        break;

      case 'phx_error':
        console.error('❌ Error en canal:', message.payload);
        break;
    }
  }

  /**
   * Maneja cambios en la base de datos PostgreSQL
   */
  private handlePostgresChange(payload: any): void {
    if (payload.table === 'status' && payload.record) {
      const newStatus: MaintenanceStatus = payload.record;
      
      // Solo procesar si realmente cambió
      if (!this.currentStatus || 
          this.currentStatus.in_maintenance !== newStatus.in_maintenance ||
          this.currentStatus.in_maintenance_sudo !== newStatus.in_maintenance_sudo ||
          this.currentStatus.maintenance_message !== newStatus.maintenance_message) {
        
        const wasInMaintenance = this.currentStatus?.in_maintenance || false;
        const wasInMaintenanceSudo = this.currentStatus?.in_maintenance_sudo || false;
        this.currentStatus = newStatus;
        this.maintenanceStatusSubject.next(newStatus);
        
        // Si salimos de mantenimiento y estamos en la página de mantenimiento, verificar callback
        if (wasInMaintenance && !newStatus.in_maintenance && this.isCurrentlyOnMaintenancePage()) {
          this.handleMaintenanceExit();
        }
        
        // Si salimos de mantenimiento SUDO y estamos en la página de mantenimiento, verificar callback
        if (wasInMaintenanceSudo && !newStatus.in_maintenance_sudo && this.isCurrentlyOnMaintenancePage()) {
          this.handleMaintenanceExit();
        }
      }
    }
  }

  /**
   * Desconecta el servicio
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    }
  }

  /**
   * Obtiene el estado actual
   */
  getCurrentStatus(): MaintenanceStatus | null {
    return this.currentStatus;
  }

  /**
   * Observable que emite true cuando el sistema está en mantenimiento
   */
  isInMaintenance(): Observable<boolean> {
    return new Observable(observer => {
      this.maintenanceStatus$.subscribe(status => {
        observer.next(status?.in_maintenance || false);
      });
    });
  }

  /**
   * Observable que emite true cuando el sistema SUDO está en mantenimiento
   */
  isInMaintenanceSudo(): Observable<boolean> {
    return new Observable(observer => {
      this.maintenanceStatus$.subscribe(status => {
        observer.next(status?.in_maintenance_sudo || false);
      });
    });
  }

  /**
   * Método helper para compatibilidad con la implementación anterior de Firebase
   * Mantiene la misma API para facilitar la migración
   */
  checkMaintenanceStatus(): Observable<boolean> {
    return this.isInMaintenance();
  }

  /**
   * Maneja la salida del mantenimiento
   */
  private handleMaintenanceExit(): void {
    const callbackUrl = this.getCallbackFromUrlParams();
    if (callbackUrl) {
      // Emitir la URL de redirección
      this.redirectAfterMaintenanceSubject.next(callbackUrl);
    }
  }

  /**
   * Determina la URL por defecto según el dominio
   */
  getDefaultRedirectUrl(): string {
    const hostname = window.location.hostname;
    
    if (hostname.includes('portal')) {
      return '/dashboard';
    } else if (hostname.includes('advisor')) {
      return '/dashboard';
    } else if (hostname.includes('admin') || hostname.includes('sudo')) {
      return '/dashboards/project';
    }
    
    // Fallback basado en el puerto o path
    const origin = window.location.origin;
    if (origin.includes(':4201')) return '/dashboard'; // portal
    if (origin.includes(':4202')) return '/dashboard'; // advisor  
    if (origin.includes(':4203')) return '/dashboards/project'; // admin
    
    return '/';
  }

  /**
   * Método principal para manejar la redirección a mantenimiento
   * Retorna la URL de mantenimiento con callback como parámetro
   */
  getMaintenanceUrlWithCallback(currentUrl?: string): string {
    let callbackUrl: string;
    
    if (currentUrl && !currentUrl.includes('/maintenance')) {
      // Si nos dan una URL y no es de mantenimiento, usarla
      // Pero si es solo una ruta relativa, obtener la URL completa
      if (currentUrl.startsWith('/')) {
        callbackUrl = window.location.origin + currentUrl;
      } else {
        callbackUrl = currentUrl;
      }
    } else {
      // Si no hay URL o ya estamos en mantenimiento, usar URL actual completa
      if (window.location.pathname.includes('/maintenance')) {
        callbackUrl = this.getDefaultRedirectUrl();
      } else {
        callbackUrl = window.location.href;
      }
    }
    
    // Retornar URL de mantenimiento con callback como parámetro
    return `/maintenance?callback=${encodeURIComponent(callbackUrl)}`;
  }

  /**
   * Verifica si estamos actualmente en la página de mantenimiento
   */
  isCurrentlyOnMaintenancePage(): boolean {
    return window.location.pathname.includes('/maintenance');
  }

  /**
   * Obtiene la URL de callback desde los parámetros de la URL actual
   */
  getCallbackFromUrlParams(): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    const callback = urlParams.get('callback');
    
    return callback ? decodeURIComponent(callback) : null;
  }

  /**
   * Obtiene la URL de callback efectiva: URL params > default
   */
  getEffectiveCallbackUrl(): string {
    // 1. Primero intentar desde parámetros URL
    const urlCallback = this.getCallbackFromUrlParams();
    if (urlCallback) {
      // Si es una URL absoluta, extraer solo el path + search + hash
      if (urlCallback.startsWith('http')) {
        try {
          const url = new URL(urlCallback);
          return url.pathname + url.search + url.hash;
        } catch (error) {
          console.error('Error parsing callback URL:', error);
          return this.getDefaultRedirectUrl();
        }
      }
      return urlCallback;
    }

    // 2. Usar URL por defecto
    return this.getDefaultRedirectUrl();
  }
}